import { useQuery } from "@apollo/client";
import { type ListMode } from "components/Tasks/ListModeButton";
import { TASK_CALENDAR_PREVIEW_QUERY } from "components/Tasks/schema";
import { buildCalendarFeedSearch } from "encamp-shared/src/tasks/calendarFeedSearch";
import { TaskCalendarScope } from "generated-graphql/graphql";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useTenant } from "hooks/useTenant";
import { useMemo } from "react";

type UseTaskCalendarPreviewOptions = {
  scope: TaskCalendarScope;
  /** The custom filter for a CUSTOM feed. */
  search?: string | null;
  /** By series lists the first few tasks; by date lists the first few occurrences. */
  listMode: ListMode;
  skip?: boolean;
};

/**
 * What a calendar feed would contain: how many tasks and events, and its first
 * few items by deadline. Builds the same search the ICS feed runs.
 */
export function useTaskCalendarPreview({
  scope,
  search,
  listMode,
  skip,
}: UseTaskCalendarPreviewOptions) {
  const { tenantId } = useTenant();
  const { user } = useCurrentUser();
  const userId = user?.id;

  const feedSearch = useMemo(() => {
    if (!tenantId || !userId) return null;
    return buildCalendarFeedSearch({
      tenantId,
      userId,
      scope,
      search,
    });
  }, [tenantId, userId, scope, search]);

  const bySeries = listMode === "series";
  const { data, previousData, loading } = useQuery(TASK_CALENDAR_PREVIEW_QUERY, {
    variables: { search: feedSearch ?? "", bySeries },
    skip: skip || !feedSearch,
  });

  // Keep showing the last result while filters change, so counts don't flicker.
  // Only the shown layout fetches items, so a result from the other layout has none.
  const result = data ?? previousData;
  return {
    loading,
    bySeries,
    taskCount: result?.tasks.count ?? null,
    eventCount: result?.events.count ?? null,
    items: (bySeries ? result?.tasks.items : result?.events.items) ?? [],
  };
}
